/**
 * The filled label the print dialog previews for one item.
 *
 * The batch fills a barcode or QR code from its whole data joined into one
 * string (`extractRecordFields` in `bulkPrint.ts`), so a placeholder the DYMO
 * designer split across several `DataString`s still fills on paper. Running
 * `customizeLabel` over the raw template can't match a split placeholder, so
 * the preview joins those first and shows what the batch prints.
 *
 * Text boxes still fill span by span here. How a record prints a text box
 * that mixes styles is the open hardware check, and the preview follows its
 * result.
 */

import { customizeLabel } from "@/utils/labels/labelUtils";
import type { OrgInfo } from "@/utils/labels/labelUtils";

// A barcode or QR code object, and a run of `DataString` siblings inside it.
const CODE_OBJECT = /<(BarcodeObject|QRCodeObject)>[\s\S]*?<\/\1>/g;
const DATA_STRING_RUN =
  /<DataString>[^<]*<\/DataString>(?:\s*<DataString>[^<]*<\/DataString>)+/g;

/** Joins each barcode and QR code's split `DataString`s into one. */
export const joinSplitDataStrings = (labelXml: string): string =>
  labelXml.replace(CODE_OBJECT, (object) =>
    object.replace(DATA_STRING_RUN, (run) => {
      const joined = Array.from(
        run.matchAll(/<DataString>([^<]*)<\/DataString>/g),
        (m) => m[1]
      ).join("");
      return `<DataString>${joined}</DataString>`;
    })
  );

/** The template filled in for one item, as the preview should draw it. */
export const buildItemPreviewXml = (
  labelXml: string,
  itemInfo: any,
  orgInfo: OrgInfo
): Promise<string> =>
  customizeLabel(joinSplitDataStrings(labelXml), itemInfo, { orgInfo });
